import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

interface PrivacySettingsProps {
  userId: string;
}

interface EntityInfo {
  id: string;
  name: string;
  type: 'networkclub' | 'community' | 'mastermind';
  role: string;
}

interface PrivacyConfig {
  baseVisibility: 'everyone' | 'connections' | 'private' | 'custom';
  visibleToEntities: string[]; // Array of entity IDs
}

const PrivacySettings: React.FC<PrivacySettingsProps> = ({ userId }) => {
  const [privacyConfig, setPrivacyConfig] = useState<PrivacyConfig>({
    baseVisibility: 'everyone',
    visibleToEntities: []
  });
  const [userEntities, setUserEntities] = useState<EntityInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showEntitySelection, setShowEntitySelection] = useState(false);

  // Load user's entities
  useEffect(() => {
    if (!userId) return;

    const membersQuery = query(
      collection(db, 'entityMembers'),
      where('userId', '==', userId),
      where('status', '==', 'active')
    );

    const unsubscribe = onSnapshot(
      membersQuery,
      async (membersSnap) => {
        try {
          const memberDocs = membersSnap.docs.map(docSnap => docSnap.data());
          const entityDocs = await Promise.all(
            memberDocs.map(async (member) => {
              const entitySnap = await getDoc(doc(db, 'entities', member.entityId));
              if (!entitySnap.exists()) return null;
              const entity = entitySnap.data() as { name: string; type: string };
              
              // Only return mastermind, community, or networkclub
              if (!['mastermind', 'community', 'networkclub'].includes(entity.type)) {
                return null;
              }

              return {
                id: entitySnap.id,
                name: entity.name,
                type: entity.type as 'mastermind' | 'community' | 'networkclub',
                role: member.role
              } as EntityInfo;
            })
          );

          const entities = entityDocs.filter(Boolean) as EntityInfo[];
          setUserEntities(entities);
        } catch (err) {
          console.error('Error loading user entities:', err);
        }
      },
      (err) => {
        console.error('Error in entities listener:', err);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  // Load privacy settings
  useEffect(() => {
    if (!userId) return;

    const loadPrivacySettings = async () => {
      try {
        const privacyRef = doc(db, 'privacySettings', userId);
        const privacySnap = await getDoc(privacyRef);
        
        if (privacySnap.exists()) {
          const data = privacySnap.data() as PrivacyConfig;
          setPrivacyConfig(data);
          setShowEntitySelection(data.baseVisibility === 'custom');
        } else {
          // Default settings
          setPrivacyConfig({
            baseVisibility: 'everyone',
            visibleToEntities: []
          });
        }
      } catch (error) {
        console.error('Error loading privacy settings:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPrivacySettings();
  }, [userId]);

  const handleSave = async () => {
    if (!userId) return;

    setSaving(true);
    try {
      const privacyRef = doc(db, 'privacySettings', userId);
      await setDoc(privacyRef, {
        ...privacyConfig,
        updatedAt: new Date()
      });
      
      console.log('✅ Privacy settings saved');
    } catch (error) {
      console.error('❌ Error saving privacy settings:', error);
      alert('Er is een fout opgetreden bij het opslaan van je privacy instellingen.');
    } finally {
      setSaving(false);
    }
  };

  const handleBaseVisibilityChange = (newVisibility: PrivacyConfig['baseVisibility']) => {
    setPrivacyConfig(prev => ({
      ...prev,
      baseVisibility: newVisibility
    }));
    setShowEntitySelection(newVisibility === 'custom');
    
    // Auto-save
    setTimeout(() => {
      handleSave();
    }, 500);
  };

  const toggleEntityVisibility = (entityId: string) => {
    setPrivacyConfig(prev => {
      const isSelected = prev.visibleToEntities.includes(entityId);
      const newEntities = isSelected
        ? prev.visibleToEntities.filter(id => id !== entityId)
        : [...prev.visibleToEntities, entityId];
      
      return {
        ...prev,
        visibleToEntities: newEntities
      };
    });
    
    // Auto-save after a short delay
    setTimeout(() => {
      handleSave();
    }, 500);
  };

  const getEntityIcon = (type: string) => {
    switch (type) {
      case 'networkclub': return '🏢';
      case 'community': return '🌍';
      case 'mastermind': return '👥';
      default: return '📁';
    }
  };

  const getEntityTypeLabel = (type: string) => {
    switch (type) {
      case 'networkclub': return 'Netwerkclub';
      case 'community': return 'Community';
      case 'mastermind': return 'Mastermind';
      default: return type;
    }
  };

  if (loading) {
    return (
      <div className="p-4 border border-gray-200 rounded-lg">
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  // Group entities by type
  const entitiesByType = userEntities.reduce((acc, entity) => {
    if (!acc[entity.type]) {
      acc[entity.type] = [];
    }
    acc[entity.type].push(entity);
    return acc;
  }, {} as Record<string, EntityInfo[]>);

  return (
    <div className="p-4 border border-gray-200 rounded-lg">
      <h3 className="font-medium text-gray-900 mb-2">Privacy</h3>
      <p className="text-sm text-gray-600 mb-3">Bepaal wie je profiel kan zien</p>
      
      {/* Base Visibility Dropdown */}
      <select 
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3"
        value={privacyConfig.baseVisibility}
        onChange={(e) => handleBaseVisibilityChange(e.target.value as PrivacyConfig['baseVisibility'])}
      >
        <option value="everyone">Iedereen op SuperKonnected</option>
        <option value="connections">Alleen mijn connecties</option>
        <option value="custom">Aangepaste zichtbaarheid (selecteer entiteiten)</option>
        <option value="private">Privé (niemand)</option>
      </select>

      {/* Entity Selection (only show if 'custom' is selected) */}
      {showEntitySelection && (
        <div className="mt-4 space-y-3">
          <div className="border-t pt-3">
            <p className="text-sm font-medium text-gray-700 mb-3">
              Selecteer in welke communities, masterminds en netwerkclubs je profiel zichtbaar is:
            </p>
            
            {userEntities.length === 0 ? (
              <div className="text-sm text-gray-500 italic py-3 text-center">
                Je bent nog geen lid van communities, masterminds of netwerkclubs
              </div>
            ) : (
              <div className="space-y-4">
                {/* Group by type */}
                {Object.entries(entitiesByType).map(([type, entities]) => (
                  <div key={type}>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      {getEntityTypeLabel(type)} ({entities.length})
                    </div>
                    <div className="space-y-2">
                      {entities.map((entity) => (
                        <label
                          key={entity.id}
                          className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-all ${
                            privacyConfig.visibleToEntities.includes(entity.id)
                              ? 'border-blue-400 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300 bg-white'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={privacyConfig.visibleToEntities.includes(entity.id)}
                            onChange={() => toggleEntityVisibility(entity.id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="text-lg">{getEntityIcon(entity.type)}</span>
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">{entity.name}</div>
                            <div className="text-xs text-gray-500">
                              {entity.role === 'owner' ? 'Organisator' : 
                               entity.role === 'organizer' ? 'Organisator' : 
                               entity.role === 'board' ? 'Bestuur' : 
                               entity.role === 'va' ? 'VA' : 'Lid'}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Selection Summary */}
            {privacyConfig.visibleToEntities.length > 0 && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="text-sm text-green-800">
                  ✓ Je profiel is zichtbaar in <strong>{privacyConfig.visibleToEntities.length}</strong> {privacyConfig.visibleToEntities.length === 1 ? 'entiteit' : 'entiteiten'}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Info Text */}
      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-xs text-blue-700">
          {privacyConfig.baseVisibility === 'everyone' && '✨ Je profiel is zichtbaar voor iedereen op SuperKonnected'}
          {privacyConfig.baseVisibility === 'connections' && '👥 Alleen je connecties kunnen je profiel zien'}
          {privacyConfig.baseVisibility === 'custom' && `🎯 Je profiel is alleen zichtbaar in de geselecteerde ${privacyConfig.visibleToEntities.length === 0 ? 'entiteiten (maak een selectie)' : 'entiteiten'}`}
          {privacyConfig.baseVisibility === 'private' && '🔒 Je profiel is privé en niet zichtbaar voor anderen'}
        </p>
      </div>

      {/* Saving Indicator */}
      {saving && (
        <div className="mt-2 text-xs text-gray-500 flex items-center space-x-2">
          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
          <span>Opslaan...</span>
        </div>
      )}
    </div>
  );
};

export default PrivacySettings;